import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { CDN_TYPE_BASE, imageUrlCDN } from 'utils';

import {
  IntlManager,
  isUserActionNeedPhotoVerification,
  needsVerification,
  NotificationManager,
  PhotoVerificationAction,
  useApplicationState,
} from '@atlas/core';
import { Icon, Label } from '@atlas/ui';

import { getBonusNotAllowedPaymentMethods } from 'components/BonusesProvider/utils';
import { useBonusesContext } from 'src/components/BonusesProvider';

import { isPartialVerification, isPaymentAllowed } from '../utils';

const PaymentMethodItem = ({
  amount,
  className = '',
  handleClick = () => {},
  methodAsLink = false,
  operation,
  paymentMethod,
  showAmount,
  to = null,
}) => {
  const location = useLocation();
  const applicationState = useApplicationState();
  const { account, bankAccounts } = applicationState;
  const { bonusesList } = useBonusesContext();
  const isUserActionNeedVerification = isUserActionNeedPhotoVerification(
    account,
    operation === 'withdrawal'
      ? PhotoVerificationAction.WITHDRAWAL
      : PhotoVerificationAction.DEPOSIT,
  );

  const openVerificationPopup = useCallback(() => {
    NotificationManager.display({
      data: {
        isPartialRegulation: isPartialVerification(account, operation),
      },
      message: 'verificationPopup',
      type: 'verificationPopup',
    });
  }, [account, operation]);

  const imageStyle = useMemo(() => {
    const style = {};
    if (paymentMethod?.desktopImageLink) {
      style.backgroundImage = `url(${imageUrlCDN(paymentMethod.desktopImageLink, CDN_TYPE_BASE)})`;
    }

    return style;
  }, [paymentMethod?.desktopImageLink]);

  const showBonusSign = useMemo(() => {
    const availableBonuses = bonusesList?.filter(bonus => {
      const notAllowedPaymentMethods = getBonusNotAllowedPaymentMethods(bonus);
      return !notAllowedPaymentMethods
        .map(methodId => parseInt(methodId))
        .includes(parseInt(paymentMethod?.id));
    });

    return !!availableBonuses?.length;
  }, [bonusesList, paymentMethod?.id]);

  const renderAmount = (label, amount, cls) => {
    const amountBoxCls = cx('profile-payments-item-amount', cls);
    return (
      <div className={amountBoxCls}>
        <Label className="profile-payments-item-amount__key" message={label} />
        <div className="profile-payments-item-amount__value">{IntlManager.formatMoney(amount)}</div>
      </div>
    );
  };

  const [, setArrowIconClass] = useState('profile-payments-item-icon');
  const [, setShouldRenderArrowIcon] = useState(false);
  const itemRef = useRef(null);

  useEffect(() => {
    const bonusIconExists = document.querySelector('.profile-payments-item-icon.with-border');
    if (bonusIconExists) {
      setArrowIconClass('profile-payments-item-icon with-bonus-icon');
    } else {
      setArrowIconClass('profile-payments-item-icon');
    }
  }, [showBonusSign]);

  useEffect(() => {
    const parentElement = itemRef.current.closest('.payments-list');
    const blockElement = itemRef.current.closest('.profile-payments__block');

    if (parentElement) {
      setShouldRenderArrowIcon(true);
    }

    if (blockElement) {
      setShouldRenderArrowIcon(false);
    }
  }, []);

  const isShowMinMaxAmount = !!(paymentMethod?.minAmount && paymentMethod?.maxAmount);

  const paymentMethodRender = (
    <>
      <div className="profile-payments-item__box">
        {showBonusSign && (
          <div className="profile-payments-item-icon">
            <Icon name="payment-atom" />
          </div>
        )}
        <div className="profile-payments-item__logo" style={imageStyle}>
          {!paymentMethod?.desktopImageLink && <Icon name="money" />}
        </div>
      </div>
      <div className="profile-payments-item__info">
        <div className="profile-payments-item__name">{paymentMethod?.name}</div>
        {showAmount ? (
          renderAmount('profile_amount', amount, 'profile-payments-item-amount-big')
        ) : isShowMinMaxAmount ? (
          <>
            {renderAmount(
              'profile_min_amount',
              paymentMethod?.minAmount,
              'profile-payments-item-amount-big',
            )}
            {renderAmount('profile_max_amount', paymentMethod?.maxAmount)}
          </>
        ) : null}
      </div>
    </>
  );

  const paymentItemClassName = cx('profile-payments-item', className);

  if (isPartialVerification(account, operation) && operation === 'withdrawal') {
    return (
      <button
        className={paymentItemClassName}
        onClick={openVerificationPopup}
        ref={itemRef}
        type="button"
      >
        {paymentMethodRender}
      </button>
    );
  }

  if (
    isPaymentAllowed(account, bankAccounts, operation, paymentMethod?.autofillProhibited) &&
    methodAsLink
  ) {
    return (
      <Link
        className={paymentItemClassName}
        ref={itemRef}
        to={to ? { pathname: to, state: location.state } : to}
      >
        {paymentMethodRender}
      </Link>
    );
  }

  const clickHandler =
    needsVerification(account, bankAccounts) && isUserActionNeedVerification
      ? openVerificationPopup
      : handleClick;

  return (
    <button className={paymentItemClassName} onClick={clickHandler} ref={itemRef} type="button">
      {paymentMethodRender}
    </button>
  );
};

PaymentMethodItem.propTypes = {
  amount: PropTypes.number,
  className: PropTypes.string,
  handleClick: PropTypes.func,
  methodAsLink: PropTypes.bool,
  operation: PropTypes.string,
  paymentMethod: PropTypes.shape({
    autofillProhibited: PropTypes.bool,
    desktopImageLink: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    maxAmount: PropTypes.number,
    minAmount: PropTypes.number,
    name: PropTypes.string,
  }),
  showAmount: PropTypes.bool,
  to: PropTypes.string,
};

export default PaymentMethodItem;
